import {
  type AssetAbcdProjection,
  type AssetAbcdProjectionDto,
  type AssetDownloadUrl,
  type AssetDownloadUrlDto,
  type AssetListResponse,
  type AssetListResponseDto,
  type AssetWithJobContext,
  type AssetWithJobContextDto,
  type CanvasPoolAsset,
  type CanvasPoolAssetDto,
  type CanvasPoolListResponse,
  type CanvasPoolListResponseDto,
  type CanvasPoolRegisterResult,
  type CanvasPoolRegisterResultDto,
} from './assets.types';
import { mapJobAssetDto } from './job.mappers';

/**
 * T10 — ABCD 投影 DTO → camelCase。opaque dict,只在 `status` 是字串時當有效;
 * known 欄位 type-guard normalize,其餘(summary)passthrough。`null`/無效 → `null`。
 */
export function mapAssetAbcdProjectionDto(
  dto: AssetAbcdProjectionDto | null | undefined,
): AssetAbcdProjection | null {
  if (!dto || typeof dto.status !== 'string') return null;
  return {
    status: dto.status,
    featureCount: typeof dto.feature_count === 'number' ? dto.feature_count : undefined,
    summary:
      dto.summary && typeof dto.summary === 'object'
        ? (dto.summary as Record<string, unknown>)
        : undefined,
    costCredits: typeof dto.cost_credits === 'number' ? dto.cost_credits : undefined,
    evaluatedAt: typeof dto.evaluated_at === 'string' ? dto.evaluated_at : undefined,
    error: typeof dto.error === 'string' ? dto.error : undefined,
    errorClass: typeof dto.error_class === 'string' ? dto.error_class : undefined,
  };
}

/**
 * Asset 域 DTO → camelCase mapper。
 * -----------------------------------------------------------------------------
 * 對應 `assetsService`。`AssetWithJobContext` 以 `JobAsset` 為 base,故 `mapAssetWithJobContextDto`
 * import `mapJobAssetDto`(`./job.mappers`)組裝 base 欄位再 attach job context —— 與
 * `assets.types.ts` 的 `extends JobAssetDto` 同一耦合,只是反映在 import 而非繼承。
 */

export function mapAssetDownloadUrlDto(dto: AssetDownloadUrlDto): AssetDownloadUrl {
  return {
    downloadUrl: dto.download_url,
    downloadUrlExpiresDatetime: dto.download_url_expires_datetime,
    mimeType: dto.mime_type,
  };
}

export function mapAssetWithJobContextDto(dto: AssetWithJobContextDto): AssetWithJobContext {
  return {
    ...mapJobAssetDto(dto),
    jobId: dto.job_id,
    jobOverallStatus: dto.job_overall_status,
    jobSubmittedDatetime: dto.job_submitted_datetime,
    abcd: mapAssetAbcdProjectionDto(dto.abcd),
  };
}

export function mapAssetListResponseDto(dto: AssetListResponseDto): AssetListResponse {
  // Round 50 defensive `?? []` — 對齊 mapJobDetailDto / mapGraphAggregateResponseDto 慣例。
  // BE 在 empty result / transient state 可能回 `assets: null`(尤其新建 project 還沒任何
  // job 跑過),`.map(null)` 直接 crash AssetLibraryPage。Mapper 邊界一次 normalize 比 caller
  // 散落 `??` 乾淨。
  return {
    assets: (dto.assets ?? []).map(mapAssetWithJobContextDto),
    nextCursor: dto.next_cursor,
  };
}

// ─── R372 畫布素材池(docs/54 §P-82 / BE docs/127)────────────────────────────────

export function mapCanvasPoolAssetDto(dto: CanvasPoolAssetDto): CanvasPoolAsset {
  return {
    assetId: dto.asset_id,
    assetKind: typeof dto.asset_kind === 'string' ? dto.asset_kind : '',
    source: typeof dto.source === 'string' ? dto.source : '',
    sourceLibraryItemId: dto.source_library_item_id ?? null,
    sourceLibraryId: dto.source_library_id ?? null,
    gcsUri: dto.gcs_uri ?? null,
    metadata:
      dto.metadata && typeof dto.metadata === 'object'
        ? (dto.metadata as Record<string, unknown>)
        : {},
    usedInNodeIds: Array.isArray(dto.used_in_node_ids) ? dto.used_in_node_ids : [],
    createdAt: typeof dto.created_at === 'string' ? dto.created_at : null,
  };
}

export function mapCanvasPoolListResponseDto(dto: CanvasPoolListResponseDto): CanvasPoolListResponse {
  return {
    items: (dto.items ?? []).map(mapCanvasPoolAssetDto),
    nextCursor: dto.next_cursor ?? null,
  };
}

export function mapCanvasPoolRegisterResultDto(
  dto: CanvasPoolRegisterResultDto,
): CanvasPoolRegisterResult {
  return { asset: mapCanvasPoolAssetDto(dto), alreadyExisted: dto.already_existed === true };
}
